/* eslint-disable */
import React from 'react'
import PropTypes from 'prop-types'
import { message } from 'antd'
import WrappedDrawer from '@/components/WrappedDrawer'
import {
  createSystemRole,
  putSystemRolePermissions,
  updateSystemRole
} from '@/service/systemControl'
import RoleCreateForm from './RoleCreateForm'

/** 从 POST /system/roles 的响应中取新建角色 id（兼容 data 为对象或标量） */
function pickCreatedRoleId(res) {
  const d = res?.data
  if (d == null) return null
  if (typeof d === 'object' && !Array.isArray(d) && 'id' in d) return d.id
  return d
}

/**
 * 角色表单抽屉：WrappedDrawer + RoleCreateForm
 * - actType=create → POST /system/roles（含 name/code/remark/dataScope），成功后 PUT /system/roles/{id}/permissions 为角色分配权限
 * - actType=edit   → PUT /system/roles/{id}，需传 record
 */
class RoleCreateDrawer extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    /** create | edit */
    actType: PropTypes.oneOf(['create', 'edit']),
    /** actType 为 edit 时必填，用于回填与更新 id */
    record: PropTypes.object,
    baseProps: PropTypes.object,
    onSuccess: PropTypes.func,
    title: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }

  static defaultProps = {
    actType: 'create',
    baseProps: {}
  }

  state = {
    /** 窄屏抽屉全宽，避免树形区域过窄 */
    drawerWidth:
      typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
        ? '100%'
        : 960
  }

  mediaQuery = null

  componentDidMount() {
    this.mediaQuery = window.matchMedia('(max-width: 767px)')
    this.applyDrawerWidth()
    this.mediaQuery.addEventListener('change', this.applyDrawerWidth)
  }

  componentWillUnmount() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.applyDrawerWidth)
    }
  }

  applyDrawerWidth = () => {
    if (!this.mediaQuery) return
    this.setState({
      drawerWidth: this.mediaQuery.matches ? '100%' : 960
    })
  }

  /** create / edit 提交 name、code、remark、dataScope；新建时 permissionIds 不进 POST body，仅在 dispatchSubmit 中从 fields 读取 */
  buildPayload = (fields) => {
    const payload = {
      name: String(fields.name || '').trim(),
      code: String(fields.code || '').trim(),
      remark: String(fields.remark || '').trim()
    }
    const rawScope = fields.dataScope
    if (rawScope != null && rawScope !== '') {
      const n = Number(rawScope)
      payload.dataScope = Number.isNaN(n) ? rawScope : n
    }
    return payload
  }

  /**
   * 按 actType 分发提交流程；新建 / 编辑可在各 case 内独立加工 ctx / payload
   * @param {'create'|'edit'} actType
   * @param {{ payload: object, record?: object, fields: object }} ctx
   */
  dispatchSubmit = async (actType, ctx) => {
    const { payload } = ctx
    switch (actType) {
      case 'create': {
        const createRes = await createSystemRole(payload)
        const roleId = pickCreatedRoleId(createRes)
        const rawIds = ctx.fields?.permissionIds
        const permissionIds = Array.isArray(rawIds)
          ? rawIds.map((k) => {
            const n = Number(k)
            return Number.isNaN(n) ? k : n
          })
          : []
        if (permissionIds.length > 0) {
          if (roleId == null) {
            message.warning('角色已创建，但未解析到角色 id，未执行权限分配')
          } else {
            const permissionIdsStr = permissionIds.map((id) => String(id)).join(',')
            const body = { permissionIds: permissionIdsStr }
            await putSystemRolePermissions(roleId, body)
          }
        }
        message.success('创建成功')
        return
      }
      case 'edit': {
        const { record } = ctx
        const id = record?.id
        if (id == null) {
          message.error('缺少角色 id')
          throw new Error('缺少角色 id')
        }
        await updateSystemRole(id, payload)
        message.success('保存成功')
        return
      }
      default:
        throw new Error(`不支持的 actType: ${actType}`)
    }
  }

  handleOk = async (form) => {
    const { onSuccess, actType, record } = this.props
    const fields = await form.current.validateFields()
    const payload = this.buildPayload(fields)
    const ctx = { payload, record, fields }

    try {
      await this.dispatchSubmit(actType, ctx)
      if (typeof onSuccess === 'function') {
        onSuccess()
      }
    } catch (e) {
      message.error(e?.message || e?.msg || (actType === 'edit' ? '保存失败' : '创建失败'))
      throw e
    }
  }

  render() {
    const { children, baseProps, title, width: widthProp, actType, record } = this.props
    const { drawerWidth } = this.state
    const width = widthProp !== undefined ? widthProp : drawerWidth
    const drawerTitle =
      title !== undefined && title !== null
        ? title
        : actType === 'edit'
          ? '编辑角色'
          : '新建角色'
    const mergedBaseProps =
      actType === 'edit'
        ? { ...baseProps, actType, initialRecord: record }
        : { ...baseProps, actType }

    return (
      <WrappedDrawer
        children={children}
        title={drawerTitle}
        width={width}
        baseComponent={<RoleCreateForm />}
        baseProps={mergedBaseProps}
        onOk={this.handleOk}
        bodyStyle={{
          padding: '12px 16px 72px'
        }}
      />
    )
  }
}

export default RoleCreateDrawer
